'use client';

import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '@/app/store';
import { useAuth } from '../../../hooks/useAuth';
import { 
  selectCurrentThemeMode, 
  selectCurrentColorTheme, 
  selectIsLoadingTheme, 
  selectThemeError,
  setThemeMode,
  setColorTheme,
  clearThemeError,
  ThemeMode,
  ColorTheme,
  THEME_MODES,
  COLOR_THEMES
} from '@/app/store/slices/themeSlice';
import { updateUserThemePreferences } from '@/app/store/thunks/themeThunks';
import { saveGuestThemePreference, updateHtmlAttributes } from '../utils/simpleThemeUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPalette, faSun, faMoon, faCheck, faTimes, faDesktop } from '@fortawesome/free-solid-svg-icons';
import { Button } from '@/components/ui/button';
import { useThemeTranslation } from '../../language/hooks/useTranslation';

interface ThemeCustomizerProps {
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

const ThemeCustomizer = React.memo<ThemeCustomizerProps>(({ 
  size = 'medium', 
  className = '' 
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useThemeTranslation();
  const currentMode = useSelector(selectCurrentThemeMode);
  const currentColorTheme = useSelector(selectCurrentColorTheme);
  const isLoading = useSelector(selectIsLoadingTheme);
  const error = useSelector(selectThemeError);
  const { isAuthenticated } = useAuth();
  
  // Use new auth system directly
  const { user } = useAuth();
  
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleModeChange = useCallback(async (newMode: ThemeMode) => {
    // Update Redux state immediately
    dispatch(setThemeMode(newMode));
    
    // Apply theme to document immediately
    updateHtmlAttributes(newMode, currentColorTheme);
    
    // Clear any previous errors
    dispatch(clearThemeError());
    
    try {
      if (isAuthenticated && user?.id) {
        // Update in database for authenticated users
        await dispatch(updateUserThemePreferences(parseInt(user.id), newMode, currentColorTheme));
      } else {
        // Save to localStorage for guest users
        saveGuestThemePreference(newMode, currentColorTheme);
      }
    } catch (error) {
      console.error('Failed to save theme mode:', error);
    }
  }, [dispatch, currentColorTheme, isAuthenticated, user?.id]);

  const handleColorThemeChange = useCallback(async (newColorTheme: ColorTheme) => {
    // Update Redux state immediately
    dispatch(setColorTheme(newColorTheme));
    
    // Apply theme to document immediately
    updateHtmlAttributes(currentMode, newColorTheme);
    
    // Clear any previous errors
    dispatch(clearThemeError());
    
    try {
      if (isAuthenticated && user?.id) {
        // Update in database for authenticated users
        await dispatch(updateUserThemePreferences(parseInt(user.id), currentMode, newColorTheme));
      } else {
        // Save to localStorage for guest users
        saveGuestThemePreference(currentMode, newColorTheme);
      }
    } catch (error) {
      console.error('Failed to save color theme:', error);
    }
  }, [dispatch, currentMode, isAuthenticated, user?.id]);

  const getDropdownWidthPx = useCallback(() => {
    switch (size) {
      case 'small': return 320;
      case 'large': return 448;
      default: return 384;
    }
  }, [size]);

  const handleToggleOpen = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleClearError = useCallback(() => {
    dispatch(clearThemeError());
  }, [dispatch]);

  return (
    <div className={`relative ${className}`}>
      {/* Theme Toggle Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={handleToggleOpen}
        disabled={isLoading}
        className="h-8 w-8 p-0 transition-colors hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2"
        title="Theme Settings"
        aria-label="Theme Settings"
      >
        {isLoading ? (
          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
        ) : (
          <FontAwesomeIcon 
            icon={currentMode === 'dark' ? faMoon : faSun} 
            className="transition-colors text-white"
          />
        )}
      </Button>

      {/* Dropdown Panel — portaled to document.body to escape the Header's
          transformed containing block (Header has translate-y for auto-hide,
          which makes `position: fixed` position relative to Header, not viewport). */}
      {isOpen && mounted && createPortal(
        <div
          className="fixed bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50"
          style={{
            top: '3rem',
            right: '0.5rem',
            width: `min(${getDropdownWidthPx()}px, calc(100vw - 1rem))`,
          }}
        >
          <div className="p-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                <FontAwesomeIcon icon={faPalette} className="mr-2" />
                {t('themeSettings')}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClose}
                className="h-6 w-6 p-0"
              >
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            </div>

            {/* Theme Mode Section */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                {t('appearance')}
              </h4>
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(THEME_MODES).map(([key, { name }]) => {
                  const isSelected = currentMode === key;
                  const themeIcons = {
                    light: faSun,
                    dark: faMoon,
                    system: faDesktop
                  };
                  
                  return (
                    <button
                      key={key}
                      onClick={() => handleModeChange(key as ThemeMode)}
                      disabled={isLoading}
                      className={`
                        relative flex flex-col items-center p-4 rounded-xl border-2 transition-all duration-200 hover:scale-105
                        ${isSelected 
                          ? 'border-primary bg-primary/10 shadow-lg' 
                          : 'border-gray-200 dark:border-gray-600 hover:border-primary/50 bg-white dark:bg-gray-700/50'
                        }
                      `}
                    >
                      {/* Selection indicator */}
                      {isSelected && (
                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                          <FontAwesomeIcon icon={faCheck} className="text-white text-xs" />
                        </div>
                      )}
                      
                      {/* Theme preview */}
                      <div className="mb-3 relative">
                        {key === 'system' ? (
                          <div className="w-8 h-8 rounded-lg overflow-hidden border border-gray-300 dark:border-gray-500">
                            <div className="w-full h-1/2 bg-white"></div>
                            <div className="w-full h-1/2 bg-gray-900"></div>
                          </div>
                        ) : key === 'light' ? (
                          <div className="w-8 h-8 rounded-lg border border-gray-300 dark:border-gray-500 flex items-center justify-center bg-gray-100 force-sun-black">
                            <FontAwesomeIcon 
                              icon={themeIcons[key as keyof typeof themeIcons]} 
                              className="text-sm"
                            />
                          </div>
                        ) : (
                          <div className="w-8 h-8 rounded-lg border border-gray-300 dark:border-gray-500 flex items-center justify-center bg-gray-900">
                            <FontAwesomeIcon 
                              icon={themeIcons[key as keyof typeof themeIcons]} 
                              className="text-sm text-white"
                            />
                          </div>
                        )}
                      </div>
                      
                      {/* Theme name */}
                      <span className={`text-xs font-medium ${
                        isSelected ? 'text-primary' : 'text-gray-700 dark:text-gray-300'
                      }`}>
                        {name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Color Theme Section */}
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                {t('colorTheme')}
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(COLOR_THEMES).map(([key, { name }]) => {
                  const isSelected = currentColorTheme === key;
                  const colorPreviews = {
                    yellow: ['#f59e0b', '#fbbf24', '#fcd34d'],
                    green: ['#10b981', '#34d399', '#6ee7b7'], 
                    blue: ['#3b82f6', '#60a5fa', '#93c5fd'],
                    purple: ['#8b5cf6', '#a78bfa', '#c4b5fd']
                  };
                  
                  return (
                    <button
                      key={key}
                      onClick={() => handleColorThemeChange(key as ColorTheme)}
                      disabled={isLoading}
                      className={`
                        relative flex items-center p-3 rounded-xl border-2 transition-all duration-200 hover:scale-105
                        ${isSelected 
                          ? 'border-primary bg-primary/10 shadow-lg' 
                          : 'border-gray-200 dark:border-gray-600 hover:border-primary/50 bg-white dark:bg-gray-700/50'
                        }
                      `}
                    >
                      {/* Selection indicator */}
                      {isSelected && (
                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                          <FontAwesomeIcon icon={faCheck} className="text-white text-xs" />
                        </div>
                      )}
                      
                      {/* Color preview */}
                      <div className="flex space-x-1 mr-3">
                        {colorPreviews[key as keyof typeof colorPreviews].map((color, index) => (
                          <div
                            key={index}
                            className="w-4 h-4 rounded-full border border-white/20"
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                      
                      {/* Color name */}
                      <span className={`text-sm font-medium ${
                        isSelected ? 'text-primary' : 'text-gray-700 dark:text-gray-300'
                      }`}>
                        {name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                <div className="text-xs text-red-500 bg-red-50 dark:bg-red-900/20 p-2 rounded">
                  <div className="flex items-center justify-between">
                    <span>Error: {error}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleClearError}
                      className="h-4 w-4 p-0 text-red-500"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Backdrop — also portaled so it overlays the full viewport, not clipped by the Header's transform */}
      {isOpen && mounted && createPortal(
        <div
          className="fixed inset-0 z-40"
          onClick={handleClose}
        />,
        document.body
      )}
    </div>
  );
});

export default ThemeCustomizer;